import React, { useState } from "react";
import { SvgIcon } from "@progress/kendo-react-common";
import * as svgIcons from "@progress/kendo-svg-icons";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { GenericMultiSelectSettingsForm } from "@presentation/components/job/components/GenericMultiSelectSettingsForm";
import { SettingsTypeEnumSchema, JobPipelineTypeSchema } from "@domain/schemas/BI";
import { Button } from "@progress/kendo-react-buttons";
import { useJobSetupStore } from "@presentation/stores/JobSetupStore";
import type { MergeNodePoolOption } from "@domain/valueObjects";

type MergeNodePoolDropdownItem = {
  value: string | null;
  title: string;
  summary?: string;
  option?: MergeNodePoolOption;
};

const formatMergeNodePoolSummary = (option: MergeNodePoolOption) => {
  const storage = option.storageBuffer ? ` / ${option.storageBuffer} storage` : "";
  const sneCombine = option.useSNECombine ? ", Uses SNE Combine" : "";
  return `${option.cpu} CPU / ${option.memory}${storage}${sneCombine}`;
};

export const JobSettingsFormBlock: React.FC = () => {
  const [isAdvancedSettingsExpanded, setIsAdvancedSettingsExpanded] = useState(false);
  const {
    CreateJobState,
    mergeNodePoolOptions,
    mergeNodePoolOptionsLoading,
    mergeNodePoolOptionsError,
    setMergeNodePoolOverride,
  } = useJobSetupStore((state) => ({
    CreateJobState: state.CreateJobState,
    mergeNodePoolOptions: state.mergeNodePoolOptions,
    mergeNodePoolOptionsLoading: state.mergeNodePoolOptionsLoading,
    mergeNodePoolOptionsError: state.mergeNodePoolOptionsError,
    setMergeNodePoolOverride: state.setMergeNodePoolOverride,
  }));

  const mergeNodePoolItems: MergeNodePoolDropdownItem[] = [
    { title: "Use default merge pool", value: null },
    ...mergeNodePoolOptions.map((option) => ({
      title: option.profile,
      summary: `${option.nodePoolName} · ${formatMergeNodePoolSummary(option)}`,
      value: option.profileKey,
      option,
    })),
  ];
  const hasKnownSelectedProfile = mergeNodePoolOptions.some((option) => option.profileKey === CreateJobState.mergeNodePoolOverride);
  const selectedMergeNodePool = mergeNodePoolItems.find((item) => item.value === (CreateJobState.mergeNodePoolOverride ?? null))
    ?? (CreateJobState.mergeNodePoolOverride && !hasKnownSelectedProfile
      ? {
          title: "Legacy node pool override",
          summary: CreateJobState.mergeNodePoolOverride,
          value: CreateJobState.mergeNodePoolOverride,
        }
      : mergeNodePoolItems[0]);
  const selectedMergeNodePoolOption = mergeNodePoolOptions.find((option) => option.profileKey === CreateJobState.mergeNodePoolOverride) ?? null;

  return (
    <div>
      <fieldset className={"k-form-fieldset"}>
        <legend className={"k-form-legend"}>
          <span title="For a given pipeline, only relevant settings from the directDIA setting schema will be applied" style={{ display: "inline-flex", alignItems: "center", gap: "4px", cursor: "help" }}>
            <strong>directDIA Settings</strong>
            <SvgIcon style={{ paddingBottom: "5px" }} icon={svgIcons.chartColumnStacked100Icon} />
          </span>
        </legend>
        <GenericMultiSelectSettingsForm settingsType={SettingsTypeEnumSchema.Experiment} />
      </fieldset>
      {CreateJobState.pipelineType !== JobPipelineTypeSchema.LibraryGeneration && (
        <fieldset className={"k-form-fieldset"}>
          <legend className={"k-form-legend"}>
            <strong>Report Schema</strong> <SvgIcon style={{ paddingBottom: "5px" }} icon={svgIcons.fileReportIcon} />
          </legend>
          <GenericMultiSelectSettingsForm settingsType={SettingsTypeEnumSchema.Report} />
        </fieldset>
      )}

      <div style={{ marginTop: "20px", marginBottom: "20px" }}>
        <Button
          onClick={() => setIsAdvancedSettingsExpanded(!isAdvancedSettingsExpanded)}
          fillMode="flat"
          themeColor="primary"
          style={{ paddingLeft: 0, fontWeight: "bold", fontSize: "1.1em", display: "flex", alignItems: "center" }}
        >
          <SvgIcon icon={isAdvancedSettingsExpanded ? svgIcons.chevronDownIcon : svgIcons.chevronRightIcon} />
          Advanced Settings
        </Button>
      </div>

      {isAdvancedSettingsExpanded && (
        <div>
          <fieldset className={"k-form-fieldset"}>
            <legend className={"k-form-legend"}>
              <strong>Merge Profile</strong> <SvgIcon style={{ paddingBottom: "5px" }} icon={svgIcons.gridLayoutIcon} />
            </legend>
            <div style={{ marginBottom: "10px" }}>
              <DropDownList
                style={{ width: "100%" }}
                data={mergeNodePoolItems}
                textField="title"
                dataItemKey="value"
                value={selectedMergeNodePool}
                loading={mergeNodePoolOptionsLoading}
                disabled={mergeNodePoolOptionsLoading || mergeNodePoolOptions.length === 0}
                valueRender={(_, value) => <span>{value?.title ?? "Use default merge pool"}</span>}
                itemRender={(li, itemProps) => {
                  const item = itemProps.dataItem as MergeNodePoolDropdownItem;
                  return React.cloneElement(li, li.props, (
                    <div style={{ display: "flex", flexDirection: "column", gap: "2px", padding: "2px 0" }}>
                      <span style={{ fontWeight: 600 }}>{item.title}</span>
                      {item.summary && <span style={{ fontSize: "0.85em", color: "#64748b", whiteSpace: "normal" }}>{item.summary}</span>}
                    </div>
                  ));
                }}
                onChange={(event) => setMergeNodePoolOverride(event.value?.value ?? null)}
              />
            </div>
            {selectedMergeNodePoolOption && (
              <div style={{ marginBottom: "10px", border: "1px solid #e2e8f0", borderRadius: "8px", padding: "12px", backgroundColor: "#f8fafc" }}>
                <div style={{ fontWeight: 600, marginBottom: "8px", color: "#0f172a" }}>
                  {selectedMergeNodePoolOption.profile}
                </div>
                <div style={{ marginBottom: "8px", fontSize: "0.9em", color: "#64748b" }}>
                  Node pool: {selectedMergeNodePoolOption.nodePoolName}
                </div>
                <div style={{ border: "1px solid #e2e8f0", borderRadius: "6px", padding: "8px", backgroundColor: "#ffffff" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: "8px", flexWrap: "wrap", marginBottom: "4px" }}>
                    <strong>{selectedMergeNodePoolOption.profile}</strong>
                    {selectedMergeNodePoolOption.useSNECombine && (
                      <span style={{ color: "#166534", fontWeight: 600 }}>
                            Uses SNE Combine
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: "0.9em", color: "#475569", display: "flex", flexWrap: "wrap", gap: "12px" }}>
                    <span>CPU: {selectedMergeNodePoolOption.cpu}</span>
                    <span>Memory: {selectedMergeNodePoolOption.memory}</span>
                    {selectedMergeNodePoolOption.storageBuffer && <span>Storage: {selectedMergeNodePoolOption.storageBuffer}</span>}
                  </div>
                </div>
              </div>
            )}
            {CreateJobState.mergeNodePoolOverride && !selectedMergeNodePoolOption && (
              <div style={{ marginBottom: "10px", border: "1px solid #f59e0b", borderRadius: "8px", padding: "12px", backgroundColor: "#fffbeb", fontSize: "0.9em", color: "#92400e" }}>
                Legacy node pool override preserved: {CreateJobState.mergeNodePoolOverride}. Select a merge profile to convert it to the new profile-based override.
              </div>
            )}
            {CreateJobState.mergeNodePoolOverride === null && mergeNodePoolOptions.length > 0 && (
              <div style={{ marginBottom: "10px", fontSize: "0.9em", color: "#64748b" }}>
                Select a merge profile to view its specs, node pool, and SNE Combine usage.
              </div>
            )}
            {mergeNodePoolOptionsError && (
              <div style={{ color: "#b91c1c", fontSize: "0.9em" }}>{mergeNodePoolOptionsError}</div>
            )}
          </fieldset>

          <fieldset className={"k-form-fieldset"}>
            <legend className={"k-form-legend"}>
              <strong>GO Annotation</strong> <SvgIcon style={{ paddingBottom: "5px" }} icon={svgIcons.clipboardCodeIcon} />
            </legend>
            <GenericMultiSelectSettingsForm settingsType={SettingsTypeEnumSchema.GO} />
          </fieldset>

          <fieldset className={"k-form-fieldset"}>
            <legend className={"k-form-legend"}>
              <strong>Custom Modification Repository</strong> <SvgIcon style={{ paddingBottom: "5px" }} icon={svgIcons.parameterBooleanIcon} />
            </legend>
            <GenericMultiSelectSettingsForm settingsType={SettingsTypeEnumSchema.Modification} />
          </fieldset>
          {CreateJobState.pipelineType !== JobPipelineTypeSchema.LibraryGeneration && (
          <fieldset className={"k-form-fieldset"}>
            <legend className={"k-form-legend"}>
              <strong>Condition setup</strong> <SvgIcon style={{ paddingBottom: "5px" }} icon={svgIcons.clipboardIcon} />
            </legend>
            <GenericMultiSelectSettingsForm settingsType={SettingsTypeEnumSchema.ConditionSetup} />
          </fieldset>
          )}
        </div>
      )}
    </div>
  );
};
